import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  Grid,
  Tooltip,
} from "@mui/material";
import useGlobalContext from "hooks/globalData";
import { FormattedMessage as T, useIntl } from "react-intl";
import { Editor as TinyMCEEditor } from "tinymce/tinymce";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  AdminListRequestOfPlaceholderFilter,
  AgreementRequest,
  AgreementResponse,
  ListResponseOfPlaceholderAdmin,
} from "services/KnowfullyAdminClient";
import { Field, withTypes } from "react-final-form";
import FieldRichText from "components/FieldRichText";
import { ReplacementText } from "types/contentTemplateTypes";
import { AgreementStatusIds, TemplateEntityIds } from "types/common-types";
import Loader from "components/Loader";
import useBoolean from "hooks/useBoolean";
import PreviewDialog from "components/PreviewTemplateDialog";
import { API_ORGANIZATION_ID } from "config/headers";
import PreviewDataSelectorDialog from "pages/ContentTemplatesPage/PreviewDataSelectorDialog";
import { useStrictQuery } from "hooks/useStrictQuery";
import { FORM_ERROR } from "final-form";
import translateError from "utils/translateError";

type AgreementContentFormProps = {
  agreement: AgreementResponse | undefined;
  onSubmit: (values: AgreementRequest) => Promise<void | { [FORM_ERROR]: any }>;
  onCancel: () => void;
};

const { Form } = withTypes<AgreementRequest>();

const AgreementContentForm = ({
  agreement,
  onSubmit,
  onCancel,
}: AgreementContentFormProps) => {
  const { agreementService } = useGlobalContext();
  const [previewOpen, previewOpenActions] = useBoolean(false);
  const [previewDataSelectorOpen, previewDataSelectorActions] = useBoolean(
    false
  );
  const [organizationForPreview, setOrganizationForPreview] = useState<
    string | null | undefined
  >(agreement?.organizationId);

  const { formatMessage: t } = useIntl();

  const init = useMemo(() => {
    const values = agreement
      ? {
          id: agreement.id,
          organizationId: agreement.organizationId,
          name: agreement.name,
          displayName: agreement.displayName,
          agreementStatusId: agreement.agreementStatusId,
          manualReplacements: agreement.manualReplacements,
          content: agreement.content,
          templateVersionId: agreement.templateVersionId,
        }
      : {};

    return AgreementRequest.fromJS(values);
  }, [agreement]);

  const fechedPlaceholders = useStrictQuery<ListResponseOfPlaceholderAdmin>(
    ["agreementService.getPlaceholders", agreement?.id],
    () =>
      agreementService.getPlaceholders(
        agreement?.id,
        AdminListRequestOfPlaceholderFilter.fromJS({
          organizationId: agreement?.organizationId,
          pageSize: -1,
          sortField: "name",
          sortDescending: false,
          parameters: {
            templateEntityId: TemplateEntityIds.SkuAgreement,
          },
        })
      ),
    {
      enabled: !!agreement?.id,
    }
  );

  const editor = useRef<TinyMCEEditor>();

  const setupEditor = useCallback((e: TinyMCEEditor) => {
    editor.current = e;
  }, []);

  if (fechedPlaceholders.isLoading) return <Loader />;
  if (fechedPlaceholders.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="templateLoadError" />
        </Alert>
      </Box>
    );
  }

  const placeholderList = fechedPlaceholders?.data?.data;

  const replacementText: ReplacementText[] = [
    { id: 0, textToReplace: "&gt;", textReplaceWith: ">" },
    { id: 0, textToReplace: "&amp;gt;", textReplaceWith: ">" },
  ];

  const isDefaultTemplate =
    agreement?.organizationId?.toLowerCase() ===
    API_ORGANIZATION_ID?.toLowerCase();

  const onPreview = async () => {
    try {
      if (!isDefaultTemplate) {
        previewOpenActions.setTrue();
      } else {
        previewDataSelectorActions.setTrue();
      }
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  const onSetPreviewData = (organizationId: string) => {
    setOrganizationForPreview(organizationId);
    previewDataSelectorActions.setFalse();
    previewOpenActions.setTrue();
  };

  const isDisabled =
    agreement?.agreementStatusId?.toUpperCase() ===
      AgreementStatusIds.Deactivated ||
    agreement?.agreementStatusId?.toUpperCase() === AgreementStatusIds.Active;

  const hasContent = !!agreement?.content;

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={init}
      render={({
        handleSubmit,
        submitting,
        submitError,
        values,
        dirty,
        form: { change },
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Field name="content" required>
                {({ input, meta }) => (
                  <Grid item xs={12} sx={{ mt: 3 }}>
                    <FieldRichText
                      key={`editor-${fechedPlaceholders.dataUpdatedAt}`}
                      input={input}
                      meta={meta}
                      replacementText={replacementText}
                      setupEditor={setupEditor}
                      disabled={isDisabled}
                      init={{
                        height: 470,
                        paste_data_images: true,
                        templates: placeholderList,
                      }}
                    />
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Box marginTop={2} marginLeft={2}>
                  <Alert severity="error">{submitError}</Alert>
                </Box>
              )}
            </Grid>

            <DialogActions>
              {submitting && <CircularProgress size={24} />}
              <Button disabled={submitting || !dirty} onClick={onCancel}>
                {t({ id: "cancelButton" })}
              </Button>

              <Tooltip title={"Preview"}>
                <div>
                  <Button
                    disabled={submitting || !hasContent}
                    onClick={onPreview}
                  >
                    {t({ id: "previewButton" })}
                  </Button>
                </div>
              </Tooltip>

              <Button
                type="submit"
                color="primary"
                disabled={submitting || !dirty || isDisabled}
              >
                {t({ id: "saveButton" })}
              </Button>
            </DialogActions>

            <PreviewDataSelectorDialog
              onCancel={previewDataSelectorActions.setFalse}
              open={previewDataSelectorOpen}
              handleConfirm={onSetPreviewData}
            />

            <PreviewDialog
              open={previewOpen}
              handleConfirm={() => previewOpenActions.setFalse()}
              templateVersionId={agreement?.templateVersionId!}
              organizationId={organizationForPreview!}
            />
          </form>
        );
      }}
    />
  );
};

export default AgreementContentForm;
